import { z } from 'zod';
import { ClassificationResultSchema, SuggestionLabelMetadataSchema, TrackSchema } from './classify';
import { EmailPreviewSchema } from './common';

/**
 * Classification-Specific Suggestion Schemas (NEW)
 * These are for the Smart Track Classification feature
 */

// Suggestion Generation Request
const ClassificationEmailSourceSchema = z.discriminatedUnion('source_type', [
  z.object({
    source_type: z.literal('unread_count'),
    count: z.number().min(1).max(100).default(50),
  }),
  z.object({
    source_type: z.literal('message_ids'),
    message_ids: z.array(z.string()).max(50),
  }),
  z.object({
    source_type: z.literal('email_table_ids'),
    email_ids: z.array(z.string().uuid()).max(50),
  }),
])

export const ClassificationGenerateSuggestionsRequestSchema = z.object({
  email_source: ClassificationEmailSourceSchema.describe('How to select emails for classification'),
  force_llm: z.boolean().default(false).optional().describe('Skip deterministic engine'),
  min_confidence: z
    .number()
    .min(0.5)
    .max(0.9)
    .default(0.7)
    .optional()
    .describe('Minimum confidence threshold to generate suggestion'),
})

export type ClassificationGenerateSuggestionsRequest = z.infer<typeof ClassificationGenerateSuggestionsRequestSchema>

// Suggestion Item (for list responses)
const ClassificationSuggestionItemSchema = z.object({
  id: z.string().uuid(),
  message_id: z.string(),
  email_subject: z.string().nullable(),
  sender_email: z.string().nullable(),
  snippet: z.string().nullable().optional(),
  track: TrackSchema,
  labels: z.array(z.string()),
  actions: z.object({
    keep_unread: z.boolean(),
    move_to_inbox: z.boolean(),
    archive: z.boolean(),
    trash: z.boolean(),
  }),
  confidence: z.number().min(0).max(1),
  rationale: z.string(),
  matchedRuleId: z.string().uuid().describe('ID of the rule that matched (or LLM sentinel UUID)'),
  status: z.enum(['pending', 'applied', 'rejected', 'expired']),
  created_at: z.string().datetime(),
  applied_at: z.string().datetime().optional(),
}).merge(SuggestionLabelMetadataSchema)

export type ClassificationSuggestionItem = z.infer<typeof ClassificationSuggestionItemSchema>

// Email Preview
export const ClassificationEmailPreviewSchema = z.object({
  id: z.string().uuid(),
  message_id: z.string(),
  subject: z.string(),
  from: z.string(),
  to: z.string(),
  date: z.string().datetime(),
  body_excerpt: z.string(),
  snippet: z.string(),
  has_attachments: z.boolean(),
  labels: z.array(z.string()),
})

export type ClassificationEmailPreview = z.infer<typeof ClassificationEmailPreviewSchema>

// Sender Insights
export const ClassificationSenderInsightsItemSchema = z.object({
  sender_email: z.string(),
  total_emails_90d: z.number(),
  open_rate: z.number().min(0).max(1),
  reply_rate: z.number().min(0).max(1),
  trash_rate: z.number().min(0).max(1),
  days_since_last_open: z.number(),
  days_since_last_reply: z.number(),
  is_in_contacts: z.boolean(),
})

export type ClassificationSenderInsightsItem = z.infer<typeof ClassificationSenderInsightsItemSchema>

// Suggestion Detail Response
export const ClassificationSuggestionDetailResponseSchema = z.object({
  id: z.string().uuid(),
  message_id: z.string(),
  track: TrackSchema,
  labels: z.array(z.string()),
  actions: z.object({
    keep_unread: z.boolean(),
    move_to_inbox: z.boolean(),
    archive: z.boolean(),
    trash: z.boolean(),
  }),
  signals: z.object({
    is_list_mail: z.boolean(),
    is_marketing: z.boolean(),
    requires_action: z.boolean(),
    action_type: z.string(),
    // #283 finding #6 - Pre-LLM may leave these null when no signal supports
    // a value. The wire schema mirrors the canonical ClassificationSignals.
    risk_type: z.string().nullable(),
    is_transaction_record: z.boolean().nullable(),
    key_evidence: z.array(z.string()),
  }),
  confidence: z.number().min(0).max(1),
  rationale: z.string(),
  status: z.enum(['pending', 'applied', 'rejected']),
  created_at: z.string().datetime(),
  applied_at: z.string().datetime().optional(),
  email_preview: ClassificationEmailPreviewSchema,
  sender_insights: ClassificationSenderInsightsItemSchema,
}).merge(SuggestionLabelMetadataSchema)

export type ClassificationSuggestionDetailResponse = z.infer<typeof ClassificationSuggestionDetailResponseSchema>

// List Suggestions Response
export const ClassificationListSuggestionsResponseSchema = z.object({
  suggestions: z.array(ClassificationSuggestionItemSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  summary: z.object({
    by_track: z.record(z.string(), z.number()),
    by_status: z.record(z.string(), z.number()),
    avg_confidence: z.number().min(0).max(1),
  }),
})

export type ClassificationListSuggestionsResponse = z.infer<typeof ClassificationListSuggestionsResponseSchema>

// Generate Suggestions Response
export const ClassificationGenerateSuggestionsResponseSchema = z.object({
  suggestion_ids: z.array(z.string().uuid()),
  email_count: z.number(),
  generated_count: z.number(),
  skipped_count: z.number(),
  processing_time_ms: z.number(),
  summary: z.object({
    by_track: z.record(z.string(), z.number()),
    avg_confidence: z.number().min(0).max(1),
  }),
})

export type ClassificationGenerateSuggestionsResponse = z.infer<typeof ClassificationGenerateSuggestionsResponseSchema>

// Apply Suggestion Requests
export const ClassificationApplySuggestionRequestSchema = z.object({
  action_overrides: z
    .object({
      keep_unread: z.boolean().optional(),
      move_to_inbox: z.boolean().optional(),
      archive: z.boolean().optional(),
      trash: z.boolean().optional(),
    })
    .optional()
    .describe('Override default actions before applying'),
})

export type ClassificationApplySuggestionRequest = z.infer<typeof ClassificationApplySuggestionRequestSchema>

export const ClassificationApplySuggestionResponseSchema = z.object({
  success: z.boolean(),
  suggestion_id: z.string().uuid(),
  message_id: z.string(),
  track: TrackSchema,
  actions_applied: z.object({
    archived: z.boolean(),
    trashed: z.boolean(),
    marked_read: z.boolean(),
    labels_applied: z.array(z.string()),
  }),
  operation_id: z.string().uuid(),
  timestamp: z.string().datetime(),
})

export type ClassificationApplySuggestionResponse = z.infer<typeof ClassificationApplySuggestionResponseSchema>

// Reject Suggestion Request
export const ClassificationRejectSuggestionRequestSchema = z.object({
  feedback_reason: z
    .enum(['incorrect_track', 'incorrect_labels', 'low_confidence', 'other'])
    .optional()
    .describe('Why the suggestion was rejected'),
  user_comment: z.string().max(500).optional().describe('Additional feedback'),
  correct_track: TrackSchema.optional().describe('If known, the correct track'),
})

export type ClassificationRejectSuggestionRequest = z.infer<typeof ClassificationRejectSuggestionRequestSchema>

// Exclude Suggestion (#296)
// Excluding a pending suggestion marks it as user_action='excluded' AND
// writes a sender_exclusion_hints row so the same sender+label combination
// is suppressed from the next scan. Body is intentionally empty - the
// path param fully identifies the row to exclude.
export const ClassificationExcludeSuggestionRequestSchema = z.object({}).strict()

export type ClassificationExcludeSuggestionRequest = z.infer<typeof ClassificationExcludeSuggestionRequestSchema>

export const ClassificationExcludeSuggestionResponseSchema = z.object({
  ok: z.literal(true),
  suggestion_id: z.string().uuid(),
  excluded_at: z.string().datetime(),
})

export type ClassificationExcludeSuggestionResponse = z.infer<typeof ClassificationExcludeSuggestionResponseSchema>

// 409 conflict body. The route returns one of these reasons when the
// suggestion has already been processed by an apply or earlier exclude.
export const ClassificationExcludeConflictResponseSchema = z.object({
  error: z.literal('CONFLICT'),
  code: z.literal('SUGGESTION_ALREADY_PROCESSED'),
  message: z.string(),
  reason: z.enum(['already_applied', 'already_excluded']),
})

export type ClassificationExcludeConflictResponse = z.infer<typeof ClassificationExcludeConflictResponseSchema>

// Batch Apply Suggestions
export const ClassificationBatchApplySuggestionsRequestSchema = z.object({
  suggestion_ids: z.array(z.string().uuid()).max(100),
  stop_on_error: z.boolean().default(false).optional(),
})

export type ClassificationBatchApplySuggestionsRequest = z.infer<typeof ClassificationBatchApplySuggestionsRequestSchema>

export const ClassificationBatchApplySuggestionsResponseSchema = z.object({
  applied: z.number(),
  failed: z.number(),
  results: z.array(
    z.object({
      suggestion_id: z.string().uuid(),
      success: z.boolean(),
      error: z.string().optional(),
      operation_id: z.string().uuid().optional(),
    })
  ),
  summary: z.object({
    total_processed: z.number(),
    total_by_track: z.record(z.string(), z.number()),
    total_labels_applied: z.number(),
  }),
})

export type ClassificationBatchApplySuggestionsResponse = z.infer<typeof ClassificationBatchApplySuggestionsResponseSchema>

// Sender Insights Response
export const ClassificationSenderInsightsResponseSchema = z.object({
  sender_email: z.string(),
  total_emails_90d: z.number(),
  open_rate: z.number().min(0).max(1),
  reply_rate: z.number().min(0).max(1),
  trash_rate: z.number().min(0).max(1),
  days_since_last_open: z.number(),
  days_since_last_reply: z.number(),
  is_in_contacts: z.boolean(),
  recent_classifications: z.array(ClassificationResultSchema).max(5),
})

export type ClassificationSenderInsightsResponse = z.infer<typeof ClassificationSenderInsightsResponseSchema>

/**
 * LEGACY Suggestion Schemas (EXISTING - KEPT FOR BACKWARDS COMPATIBILITY)
 */

export const SuggestionEmailPreviewSchema = EmailPreviewSchema

export const GetSuggestionPreviewQuerySchema = z.object({
    limit: z.string().regex(/^\d+$/).transform(Number).optional().default(20),
});

export const GetSuggestionsQuerySchema = z.object({
    previewLimit: z.string().regex(/^\d+$/).transform(Number).optional().default(5),
});

export const SuggestionIdParamsSchema = z.object({
    suggestionId: z.string(),
});

export type SuggestionEmailPreview = z.infer<typeof EmailPreviewSchema>
// Removed legacy LabelAllSummaryResponse in favor of hierarchical parent in GetSuggestionTreeResponseSchema

// Hierarchical suggestions with ordered actions

export const SuggestionActionTypeSchema = z.enum([
    'apply_label',
    'mark_read',
    'archive',
    'move_to_label',
    'delete',
]);

export const ApplyLabelParamsSchema = z.object({
    label: z.string().min(1),
});

export const MoveToLabelParamsSchema = z.object({
    label: z.string().min(1),
});

export const SuggestionActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('mark_read') }),
    z.object({ type: z.literal('archive') }),
    z.object({ type: z.literal('delete') }),
    z.object({ type: z.literal('apply_label'), params: ApplyLabelParamsSchema }),
    z.object({ type: z.literal('move_to_label'), params: MoveToLabelParamsSchema }),
]);

export const SuggestionKindSchema = z.enum(['parent', 'category', 'custom']);

export const SuggestionItemSchema = z.object({
    id: z.string(),
    parent_id: z.string().nullable().default(null),
    kind: SuggestionKindSchema.default('category'),
    title: z.string(),
    description: z.string().optional().default(''),
    category: z.string().optional(),
    label: z.string().optional(),
    affected_email_count: z.number().int().nonnegative().default(0),
    preview_emails: z.array(EmailPreviewSchema).default([]),
    actions: z.array(SuggestionActionSchema).default([]),
});

export const GetSuggestionItemsResponseSchema = z.object({
    items: z.array(SuggestionItemSchema),
});

export const GetSuggestionTreeResponseSchema = z.object({
    rootId: z.string().nullable().optional(),
    items: z.array(SuggestionItemSchema),
});

export const ApplySuggestionRequestSchema = z.object({
    suggestionId: z.string(),
    childIds: z.array(z.string()).optional(),
    emailIds: z.array(z.string()).optional(),
    actions: z.array(SuggestionActionSchema).optional(),
});

export const ApplySuggestionResultSchema = z.object({
    updated_count: z.number().int().nonnegative(),
    applied: z.boolean().default(true),
});

export type SuggestionActionType = z.infer<typeof SuggestionActionTypeSchema>
export type SuggestionAction = z.infer<typeof SuggestionActionSchema>
export type SuggestionItem = z.infer<typeof SuggestionItemSchema>
export type GetSuggestionItemsResponse = z.infer<typeof GetSuggestionItemsResponseSchema>
export type GetSuggestionTreeResponse = z.infer<typeof GetSuggestionTreeResponseSchema>
export type ApplySuggestionRequest = z.infer<typeof ApplySuggestionRequestSchema>
export type ApplySuggestionResult = z.infer<typeof ApplySuggestionResultSchema>
